import { motion } from "framer-motion";
import React, { useEffect, useRef, useState, useMemo } from "react";

// Helper to build animation keyframes
const buildKeyframes = (from, steps) => {
  const keys = new Set([
    ...Object.keys(from),
    ...steps.flatMap((s) => Object.keys(s)),
  ]);
  const keyframes = {};
  keys.forEach((k) => {
    keyframes[k] = [from[k], ...steps.map((s) => s[k])];
  });
  return keyframes;
};

// Helper to split string into lines using \n or <br>
const splitTextToLines = (text) => {
  return text
    .replace(/<br\s*\/?>/gi, "\n") // Replace <br> or <br/> with newline
    .split("\n");
};

const BlurText = ({
  text = "",
  delay = 200,
  className = "",
  animateBy = "words",
  direction = "top",
  threshold = 0.1,
  rootMargin = "0px",
  animationFrom,
  animationTo,
  easing = (t) => t,
  onAnimationComplete,
  stepDuration = 0.35,
}) => {
  const lines = splitTextToLines(text);
  const [inView, setInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(ref.current);
        }
      },
      { threshold, rootMargin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  const defaultFrom = useMemo(
    () =>
      direction === "top"
        ? { filter: "blur(100px)", opacity: 0, y: -50 }
        : { filter: "blur(100px)", opacity: 0, y: 50 },
    [direction]
  );

  const defaultTo = useMemo(
    () => [
      {
        filter: "blur(5px)",
        opacity: 0.5,
        y: direction === "top" ? 5 : -5,
      },
      { filter: "blur(0px)", opacity: 1, y: 0 },
    ],
    [direction]
  );

  const fromSnapshot = animationFrom ?? defaultFrom;
  const toSnapshots = animationTo ?? defaultTo;

  const stepCount = toSnapshots.length + 1;
  const totalDuration = stepDuration * (stepCount - 1);
  const times = Array.from({ length: stepCount }, (_, i) =>
    stepCount === 1 ? 0 : i / (stepCount - 1)
  );

  // For unique delays across lines&words
  const elementCountPerLine = lines.map((line) =>
    animateBy === "words" ? line.split(" ").length : line.split("").length
  );
  // For delay calculation
  const getGlobalIndex = (lineIdx, idx) =>
    elementCountPerLine.slice(0, lineIdx).reduce((a, b) => a + b, 0) + idx;

  return (
   <p
  ref={ref}
  className={className}
  style={{
    margin: 0,
    padding: 0,
    width: "100%",
    wordBreak: "break-word",
    whiteSpace: "pre-line", // <-- This makes \n act as a line break!
    textAlign : "start"
  }}
>
      {lines.map((line, lineIdx) => {
        const elements =
          animateBy === "words" ? line.split(" ") : line.split("");
        return (
          <React.Fragment key={lineIdx}>
            {elements.map((segment, idx) => {
              const animateKeyframes = buildKeyframes(
                fromSnapshot,
                toSnapshots
              );
              const globalIndex = getGlobalIndex(lineIdx, idx);
              const spanTransition = {
                duration: totalDuration,
                times,
                delay: (globalIndex * delay) / 1000,
                ease: easing,
              };
              return (
                <motion.span
                  className="inline-block will-change-[transform,filter,opacity]"
                  key={idx}
                  initial={fromSnapshot}
                  animate={inView ? animateKeyframes : fromSnapshot}
                  transition={spanTransition}
                  onAnimationComplete={
                    lineIdx === lines.length - 1 && idx === elements.length - 1
                      ? onAnimationComplete
                      : undefined
                  }
                  style={{ whiteSpace: "pre" }}
                >
                  {segment === " " ? "\u00A0" : segment}
                  {animateBy === "words" &&
                    idx < elements.length - 1 &&
                    "\u00A0"}
                </motion.span>
              );
            })}
            {lineIdx < lines.length - 1 && <br />}
          </React.Fragment>
        );
      })}
    </p>
  );
};

export default BlurText;
